import React from "react";
import classes from "./QuizBadgeResult.module.scss";
import badgeGetImage from "../../static/images/badge-get.png";
import SimpleButton from "../../atoms/simple-button/SimpleButton";
import { useLocation } from "react-router-dom";
import charImage from "../../static/images/quiz/char-bow.png";
import rabbitWinkNormal from "../../static/images/rabbit-wink-normal.png";
import childChar1 from "../../static/images/character/child-char-1.png";
import childChar2 from "../../static/images/character/child-char-2.png";
import childChar3 from "../../static/images/character/child-char-3.png";
import childChar4 from "../../static/images/character/child-char-4.png";
import childChar5 from "../../static/images/character/child-char-5.png";
import childChar6 from "../../static/images/character/child-char-6.png";
import childChar7 from "../../static/images/character/child-char-7.png";
import newPoint from "../../static/images/quiz/new-point.svg";
import currentPoint from "../../static/images/quiz/current-point.svg";
import noPoint from "../../static/images/quiz/no-point.svg";

interface QuizBadgeResultProps {
  data: QuizBadgeResultData;
  close: () => void;
}

export type QuizBadgeResultData = {
  quizId: string;
  badgeExplanation: string;
  badgeImageData: string;
  badgeTitle: string;
  isAcquiredBadgeType: boolean;
  supportLink: string;
  quizType: string;
  preAcquiredPoint: number;
  currentAcquiredPoint: number;
  prevBadgeTitle: string;
  isAcquiredPoint: boolean;
  acquiredPointOwnerList: {
    pointOwnerId: number;
    ownerType: "user" | "child";
    ownerName: string;
    iconType: string | null;
    displayOrder: number;
  }[];
};

const childIcons: Record<string, string> = {
  "child-char-1": childChar1,
  "child-char-2": childChar2,
  "child-char-3": childChar3,
  "child-char-4": childChar4,
  "child-char-5": childChar5,
  "child-char-6": childChar6,
  "child-char-7": childChar7,
};

// バッチの種類や状況によって表示内容が異なる&アクションが完全に消えるかわからないため、一旦別でコンポーネント化する
const QuizBadgeResult = (props: QuizBadgeResultProps) => {
  const POINTS_PER_CORRECT = 100;
  const location = useLocation();
  const previousPoint = props.data.preAcquiredPoint ?? 0;
  const earnedPoint = props.data.currentAcquiredPoint ?? 0;
  const currentPointTotal = previousPoint + earnedPoint;
  const acquiredPointOwners = (props.data.acquiredPointOwnerList ?? [])
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, 5);

  const close = (path: string) => {
    props.close();

    // 再読み込みこみさせたいので
    window.location.replace(
      `${window.location.origin}${process.env.PUBLIC_URL}${path}`
    );
  };

  const getPointImage = (index: number) => {
    const point = index * POINTS_PER_CORRECT;
    if (point <= previousPoint) {
      return currentPoint;
    }

    if (point <= currentPointTotal) {
      return newPoint;
    }

    return noPoint;
  };

  const getOwnerImage = (
    owner: QuizBadgeResultData["acquiredPointOwnerList"][number]
  ) => {
    if (owner.ownerType === "user") {
      return rabbitWinkNormal;
    }
    return childIcons[owner.iconType ?? ""] ?? childChar1;
  };

  const getOwnerLabel = (
    owner: QuizBadgeResultData["acquiredPointOwnerList"][number]
  ) => {
    if (owner.ownerType === "user") {
      return "あなた";
    }
    return owner.ownerName || "こども";
  };

  const formatOwnerLabel = (label: string) => {
    const characters = Array.from(label);
    if (characters.length <= 3) {
      return label;
    }
    return `${characters.slice(0, 3).join("")}...`;
  };

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <img
          className={classes.ribbonImage}
          src={badgeGetImage}
          alt={badgeGetImage}
        />
        <div className={classes.title}>{props.data.badgeTitle}</div>
        {props.data.prevBadgeTitle != null && (
          <div className={classes.preTitle}>
            前回：{props.data.prevBadgeTitle}
          </div>
        )}
        {props.data.isAcquiredBadgeType ? (
          <>
            <img
              className={classes.image}
              src={props.data.badgeImageData}
              alt={props.data.badgeImageData}
            />
          </>
        ) : (
          <div className={classes.nonBadgeArea}>
            <img src={charImage} alt={charImage} />
            <div className={classes.nonBadgeText}>おつかれさまでした！</div>
          </div>
        )}
        <div className={classes.text}>{props.data.badgeExplanation}</div>
        <div className={classes.pointContainer}>
          <div className={classes.pointLine}>
            <img
              className={classes.point}
              src={getPointImage(1)}
              alt={getPointImage(1)}
            />
            <img
              className={classes.point}
              src={getPointImage(2)}
              alt={getPointImage(2)}
            />
            <img
              className={classes.point}
              src={getPointImage(3)}
              alt={getPointImage(3)}
            />
            <img
              className={classes.point}
              src={getPointImage(4)}
              alt={getPointImage(4)}
            />
            <img
              className={classes.point}
              src={getPointImage(5)}
              alt={getPointImage(5)}
            />
          </div>
          <div className={classes.pointLine}>
            <img
              className={classes.point}
              src={getPointImage(6)}
              alt={getPointImage(6)}
            />
            <img
              className={classes.point}
              src={getPointImage(7)}
              alt={getPointImage(7)}
            />
            <img
              className={classes.point}
              src={getPointImage(8)}
              alt={getPointImage(8)}
            />
            <img
              className={classes.point}
              src={getPointImage(9)}
              alt={getPointImage(9)}
            />
            <img
              className={classes.point}
              src={getPointImage(10)}
              alt={getPointImage(10)}
            />
          </div>
          {earnedPoint > 0 && acquiredPointOwners.length > 0 && (
            <>
              <div className={classes.pointOwnerList}>
                {acquiredPointOwners.map((owner) => {
                  const label = getOwnerLabel(owner);
                  return (
                    <div
                      key={owner.pointOwnerId}
                      className={classes.pointOwnerItem}
                    >
                      <img src={getOwnerImage(owner)} alt={label} />
                      <span>{formatOwnerLabel(label)}</span>
                    </div>
                  );
                })}
              </div>
              <div className={classes.ownerPointText}>
                {earnedPoint.toLocaleString("ja-JP")}ptずつ獲得!
              </div>
            </>
          )}
        </div>
        <div className={classes.buttonWrapper}>
          <SimpleButton
            label="ほかのクイズに挑戦する"
            onClick={() => close(`/quiz`)}
            width="calc(100% - 32rem)"
          />
        </div>
        <div className={classes.buttonWrapper}>
          <SimpleButton
            label="もういちど挑戦してみる"
            onClick={() => close(location.pathname + location.search)}
            width="calc(100% - 32rem)"
            color="black"
          />
        </div>
      </div>
    </div>
  );
};

export default QuizBadgeResult;
